import {
  Box,
  DataList,
  Flex,
  Button,
  Dialog,
  TextArea,
  Text,
  Badge,
  Spinner,
} from "@radix-ui/themes";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { ProofLocationMap } from "../../ProofLocationMap";
import { readExifDataFromUrl, type ExifGpsData } from "../../../lib/exif";
import { formatDistance } from "../../../lib/format-distance";
import {
  fetchProofById,
  type PendingProof,
} from "../../../state/proofs-to-approve";

function getProofStore(proof: PendingProof) {
  if (proof.target === "store") {
    return proof.store_proof?.store ?? null;
  }
  return proof.product_proof?.store_activity?.store ?? null;
}

function getProofTypeLabel(
  proof: PendingProof,
  t: (key: string) => string,
): string {
  if (proof.target === "store") {
    return t("proofTypes.storeFront");
  }
  const requirement = proof.product_proof?.product_requirement;
  if (requirement) {
    return requirement.description;
  }
  return t("proofTypes.product");
}

interface ApprobationViewProps {
  proofId: number;
  proofsLeft?: number | null;
  onDecision?: () => void;
}

export function ApprobationView({
  proofId,
  proofsLeft,
  onDecision,
}: ApprobationViewProps) {
  const { t } = useTranslation();
  const [proof, setProof] = useState<PendingProof | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [exifData, setExifData] = useState<ExifGpsData | null>(null);
  const [exifLoading, setExifLoading] = useState(false);

  const loadProof = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchProofById(proofId);
      setProof(data);
    } finally {
      setLoading(false);
    }
  }, [proofId]);

  useEffect(() => {
    loadProof();
  }, [loadProof]);

  useEffect(() => {
    setImageLoaded(false);
    setExifData(null);
    if (proof?.image_url) {
      const fn = async () => {
        try {
          const { data, error } = await supabase.storage
            .from("proof-images")
            .createSignedUrl(proof.image_url, 3600);

          if (error) {
            console.error("Error creating signed URL:", error);
            return;
          }

          if (data?.signedUrl) {
            setImageUrl(data.signedUrl);

            setExifLoading(true);
            const exif = await readExifDataFromUrl(data.signedUrl);
            setExifData(exif);
            setExifLoading(false);
          }
        } catch (e) {
          console.error("Exception creating signed URL:", e);
        }
      };
      fn();
    }
  }, [proof]);

  const handleAccept = async () => {
    if (!proof?.id) return;

    const { error } = await supabase
      .from("proofs")
      .update({ status: "approved" })
      .eq("id", proof.id);

    if (error) {
      console.error("Error accepting proof:", error);
      return;
    }

    onDecision?.();
    await loadProof();
  };

  const handleReject = async () => {
    if (!proof?.id || !rejectReason.trim()) return;

    const { error } = await supabase
      .from("proofs")
      .update({ status: "rejected", rejection_reason: rejectReason.trim() })
      .eq("id", proof.id);

    if (error) {
      console.error("Error rejecting proof:", error);
      return;
    }

    setRejectDialogOpen(false);
    setRejectReason("");
    onDecision?.();
    await loadProof();
  };

  if (loading) {
    return (
      <Flex
        p="4"
        justify="center"
        align="center"
        style={{ minHeight: "200px" }}
      >
        <Spinner size="3" />
      </Flex>
    );
  }

  if (!proof) {
    return (
      <Flex
        p="4"
        justify="center"
        align="center"
        style={{ minHeight: "200px" }}
      >
        <Text color="gray" size="3">
          {t("approbation.proofNotFound")}
        </Text>
      </Flex>
    );
  }

  const store = getProofStore(proof);
  const proofTypeLabel = getProofTypeLabel(proof, t);
  const isPending = proof.status === "pending";

  return (
    <Flex p="4" gap="4">
      <Box>
        <img
          src={imageUrl}
          alt="Proof"
          style={{ maxWidth: "100%", maxHeight: "500px", objectFit: "contain" }}
          onLoad={() => setImageLoaded(true)}
        />
      </Box>
      <Box>
        <DataList.Root>
          {proofsLeft != null && (
            <DataList.Item>
              <DataList.Label minWidth="88px">
                {t("approbation.proofsLeft")}
              </DataList.Label>
              <DataList.Value>{proofsLeft}</DataList.Value>
            </DataList.Item>
          )}
          {!isPending && (
            <DataList.Item>
              <DataList.Label minWidth="88px">
                {t("approbation.proofStatus")}
              </DataList.Label>
              <DataList.Value>
                <Badge color={proof.status === "approved" ? "green" : "red"}>
                  {proof.status === "approved"
                    ? t("approbation.accepted")
                    : t("approbation.rejected")}
                </Badge>
              </DataList.Value>
            </DataList.Item>
          )}
          <DataList.Item>
            <DataList.Label minWidth="88px">
              {t("approbation.proofType")}
            </DataList.Label>
            <DataList.Value>{proofTypeLabel}</DataList.Value>
          </DataList.Item>
          {proof.target === "product" && (
            <DataList.Item>
              <DataList.Label minWidth="88px">
                {t("approbation.product")}
              </DataList.Label>
              <DataList.Value>
                {proof.product_proof?.store_activity?.marketing_activity
                  ?.product?.name ?? "—"}
              </DataList.Value>
            </DataList.Item>
          )}
          <DataList.Item>
            <DataList.Label minWidth="88px">
              {t("approbation.store")}
            </DataList.Label>
            <DataList.Value>{store?.name ?? "—"}</DataList.Value>
          </DataList.Item>
          {proof.target === "product" && (
            <DataList.Item>
              <DataList.Label minWidth="88px">
                {t("approbation.activity")}
              </DataList.Label>
              <DataList.Value>
                {proof.product_proof?.store_activity?.marketing_activity
                  ?.marketing_activity_type?.name ?? "—"}
              </DataList.Value>
            </DataList.Item>
          )}
          <DataList.Item>
            <DataList.Label minWidth="88px">
              {t("approbation.distance")}
            </DataList.Label>
            <DataList.Value>
              {proof?.distance_from_store != null
                ? formatDistance(proof.distance_from_store)
                : "—"}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label minWidth="88px">
              {t("approbation.repName")}
            </DataList.Label>
            <DataList.Value>{proof.rep_user?.full_name ?? "—"}</DataList.Value>
          </DataList.Item>
          {proof.status === "rejected" && proof.rejection_reason && (
            <DataList.Item>
              <DataList.Label minWidth="88px">
                {t("approbation.rejectionReason")}
              </DataList.Label>
              <DataList.Value>{proof.rejection_reason}</DataList.Value>
            </DataList.Item>
          )}
        </DataList.Root>

        {/* EXIF Data Section */}
        <Text size="2" weight="bold" mt="4" mb="2" style={{ display: "block" }}>
          {t("approbation.exifData")}
        </Text>
        <DataList.Root>
          {exifLoading ? (
            <DataList.Item>
              <DataList.Label minWidth="88px">
                {t("status.pending")}
              </DataList.Label>
              <DataList.Value>{t("common.loading")}</DataList.Value>
            </DataList.Item>
          ) : exifData ? (
            <>
              <DataList.Item>
                <DataList.Label minWidth="88px">
                  {t("approbation.exifGps")}
                </DataList.Label>
                <DataList.Value>
                  {exifData.latitude.toFixed(6)},{" "}
                  {exifData.longitude.toFixed(6)}
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label minWidth="88px">
                  {t("approbation.exifDate")}
                </DataList.Label>
                <DataList.Value>
                  {exifData.timestamp.toLocaleString()}
                </DataList.Value>
              </DataList.Item>
            </>
          ) : (
            <DataList.Item>
              <DataList.Label minWidth="88px">
                {t("status.pending")}
              </DataList.Label>
              <DataList.Value>
                <Text color="orange">{t("approbation.noExifData")}</Text>
              </DataList.Value>
            </DataList.Item>
          )}
        </DataList.Root>

        {isPending && (
          <Flex gap="3" mt="4">
            <Button color="green" style={{ flex: 1 }} onClick={handleAccept}>
              {t("approbation.accept")}
            </Button>
            <Dialog.Root
              open={rejectDialogOpen}
              onOpenChange={setRejectDialogOpen}
            >
              <Dialog.Trigger>
                <Button color="red" style={{ flex: 1 }}>
                  {t("approbation.reject")}
                </Button>
              </Dialog.Trigger>
              <Dialog.Content maxWidth="450px">
                <Dialog.Title>{t("approbation.rejectProof")}</Dialog.Title>
                <Dialog.Description size="2" mb="4">
                  {t("approbation.rejectDescription")}
                </Dialog.Description>
                <TextArea
                  placeholder={t("approbation.rejectPlaceholder")}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
                <Flex gap="3" mt="4" justify="end">
                  <Dialog.Close>
                    <Button variant="soft" color="gray">
                      {t("common.cancel")}
                    </Button>
                  </Dialog.Close>
                  <Button
                    color="red"
                    onClick={handleReject}
                    disabled={!rejectReason.trim()}
                  >
                    {t("approbation.reject")}
                  </Button>
                </Flex>
              </Dialog.Content>
            </Dialog.Root>
          </Flex>
        )}

        {imageLoaded &&
          store?.latitude != null &&
          store?.longitude != null &&
          proof?.latitude != null &&
          proof?.longitude != null && (
            <Box mt="4">
              <ProofLocationMap
                storeLocation={{
                  latitude: store.latitude,
                  longitude: store.longitude,
                }}
                proofLocation={{
                  latitude: proof.latitude,
                  longitude: proof.longitude,
                }}
                storeName={store.name}
                proofAccuracy={proof.accuracy}
              />
            </Box>
          )}
      </Box>
    </Flex>
  );
}
